// src/components/common/PageHeader/PageHeader.jsx
import React from 'react';
import styles from './PageHeader.module.css';

/**
 * PageHeader Component
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description/subtitle
 * @param {ReactElement} props.icon - Icon to display before title
 * @param {string} props.iconColor - Icon color (default: uses CSS variable --color-primary)
 * @param {ReactNode} props.actions - Action buttons/components to display on the right
 * @param {ReactNode} props.breadcrumbs - Breadcrumb navigation
 * @param {ReactNode} props.tabs - Tab navigation
 * @param {ReactNode} props.extra - Additional content below title/description
 * @param {string} props.className - Additional CSS class for the header
 * @param {boolean} props.sticky - Make header sticky on scroll
 * @param {Object} props.style - Inline styles
 * @param {boolean} props.centerActions - Vertically center actions against the title block
 */
const PageHeader = ({
  title,
  description,
  icon: Icon,
  iconColor = 'var(--color-primary)',
  actions,
  breadcrumbs,
  tabs,
  extra,
  className = '',
  sticky = false,
  style,
  centerActions = false
}) => {
  const headerClassName = `
    ${styles.pageHeader} 
    ${sticky ? styles.sticky : ''} 
    ${className}
  `.trim();

  return (
    <div className={headerClassName} style={style}>
      {breadcrumbs && (
        <div className={styles.breadcrumbs}>
          {breadcrumbs}
        </div>
      )}

      <div className={styles.headerContent}>
        <div className={styles.headerLeft}>
          <div className={styles.headerTitle}>
            {Icon && (
              <div className={styles.iconWrapper} style={{ color: iconColor }}>
                <Icon size={24} />
              </div>
            )}
            
            <div className={styles.titleContent}>
              <h1>{title}</h1>
              {description && <p>{description}</p>}
            </div>
          </div>

          {extra && (
            <div className={styles.headerExtra}>
              {extra}
            </div>
          )}
        </div>

        {actions && (
          <div className={`${styles.headerActions} ${centerActions ? styles.headerActionsCentered : ''}`}>
            {actions}
          </div>
        )}
      </div>

      {tabs && (
        <div className={styles.headerTabs}>
          {tabs}
        </div>
      )}
    </div>
  );
};

export default PageHeader;